/**
 * PureOne Performance Optimizations
 * Implements lazy loading, image optimization, and performance enhancements
 */

class PureOnePerformance {
  constructor() {
    this.init();
  }

  init() {
    // Check for native lazy loading support
    this.hasNativeLazyLoad = 'loading' in HTMLImageElement.prototype;

    // Initialize performance optimizations
    this.setupLazyLoading();
    this.optimizeImages();
    this.deferNonCriticalCSS();
    this.preloadCriticalAssets();
    this.setupResourceHints();
    this.enableSmoothScrolling();
    this.optimizeAnimations();
  }

  setupLazyLoading() {
    // Get all images and iframes that should be lazy loaded
    const lazyElements = document.querySelectorAll('img[data-lazy], iframe[data-lazy], video[data-lazy]');

    if (this.hasNativeLazyLoad) {
      // Use native lazy loading
      lazyElements.forEach(element => {
        element.loading = 'lazy';

        // Move data-src to src
        if (element.dataset.src) {
          element.src = element.dataset.src;
          delete element.dataset.src;
        }

        // Handle srcset
        if (element.dataset.srcset) {
          element.srcset = element.dataset.srcset;
          delete element.dataset.srcset;
        }
      });
    } else {
      // Fallback to Intersection Observer
      this.lazyLoadWithIntersectionObserver(lazyElements);
    }
  }

  lazyLoadWithIntersectionObserver(elements) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;

          // Load the resource
          if (element.dataset.src) {
            element.src = element.dataset.src;
            delete element.dataset.src;
          }

          if (element.dataset.srcset) {
            element.srcset = element.dataset.srcset;
            delete element.dataset.srcset;
          }

          // Add loaded class for animations
          element.classList.add('lazy-loaded');

          // Stop observing this element
          observer.unobserve(element);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    });

    elements.forEach(element => {
      imageObserver.observe(element);
    });
  }

  optimizeImages() {
    // Add loading="lazy" to all images without it
    document.querySelectorAll('img:not([loading])').forEach(img => {
      // Skip above-the-fold images
      const rect = img.getBoundingClientRect();
      if (rect.top > window.innerHeight) {
        img.loading = 'lazy';
      }

      // Add decoding="async" for better performance
      if (!img.decoding) {
        img.decoding = 'async';
      }

      // Add aspect ratio to prevent layout shift
      if (img.width && img.height && !img.style.aspectRatio) {
        img.style.aspectRatio = `${img.width} / ${img.height}`;
      }
    });

    // Optimize background images
    this.optimizeBackgroundImages();
  }

  optimizeBackgroundImages() {
    const elements = document.querySelectorAll('[data-bg-src]');

    const bgObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const bgSrc = element.dataset.bgSrc;

          if (bgSrc) {
            element.style.backgroundImage = `url(${bgSrc})`;
            delete element.dataset.bgSrc;
            element.classList.add('bg-loaded');
          }

          bgObserver.unobserve(element);
        }
      });
    }, {
      rootMargin: '100px 0px',
      threshold: 0
    });

    elements.forEach(element => {
      bgObserver.observe(element);
    });
  }

  deferNonCriticalCSS() {
    // Find all non-critical stylesheets
    const nonCriticalStyles = document.querySelectorAll('link[rel="stylesheet"][data-defer]');

    nonCriticalStyles.forEach(link => {
      // Change rel to preload
      link.rel = 'preload';
      link.as = 'style';

      // Load stylesheet when idle
      link.addEventListener('load', function() {
        this.rel = 'stylesheet';
      });
    });
  }

  preloadCriticalAssets() {
    // Preload hero image
    const heroImage = document.querySelector('.hero-image img');
    if (heroImage && heroImage.src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = heroImage.src;
      document.head.appendChild(link);
    }
  }

  setupResourceHints() {
    // DNS prefetch for external resources
    const domains = [
      'https://fonts.googleapis.com',
      'https://www.google-analytics.com',
      'https://cdn.jsdelivr.net'
    ];

    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);

      // Also add preconnect for critical domains
      if (domain.includes('googleapis')) {
        const preconnect = document.createElement('link');
        preconnect.rel = 'preconnect';
        preconnect.href = domain;
        preconnect.crossOrigin = 'anonymous';
        document.head.appendChild(preconnect);
      }
    });
  }

  enableSmoothScrolling() {
    // Progressive enhancement for smooth scrolling
    if ('scrollBehavior' in document.documentElement.style) {
      document.documentElement.style.scrollBehavior = 'smooth';
    }

    // Optimize scroll performance
    let scrollTimer;
    window.addEventListener('scroll', () => {
      document.body.classList.add('is-scrolling');

      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        document.body.classList.remove('is-scrolling');
      }, 100);
    }, { passive: true });
  }

  optimizeAnimations() {
    // Pause animations when not in viewport
    const animatedElements = document.querySelectorAll('[data-animation]');

    const animationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animationPlayState = 'running';
        } else {
          entry.target.style.animationPlayState = 'paused';
        }
      });
    }, {
      threshold: 0.1
    });

    animatedElements.forEach(element => {
      element.style.animationPlayState = 'paused';
      animationObserver.observe(element);
    });

    // Reduce animations for users who prefer reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.classList.add('reduce-motion');
    }
  }

  // Utility method to defer JavaScript execution
  static deferScript(src, callback) {
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;

    if (callback) {
      script.onload = callback;
    }

    document.body.appendChild(script);
  }

  // Method to load component on demand
  static loadComponent(componentName) {
    return new Promise((resolve, reject) => {
      import(`./components/${componentName}.js`)
        .then(module => {
          resolve(module.default);
        })
        .catch(error => {
          console.error(`Failed to load component: ${componentName}`, error);
          reject(error);
        });
    });
  }

  // Image format detection and optimization
  static getOptimalImageFormat() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;

    // Check WebP support
    const supportsWebP = canvas.toDataURL('image/webp').indexOf('image/webp') === 5;

    // Check AVIF support (modern browsers)
    const supportsAvif = canvas.toDataURL('image/avif').indexOf('image/avif') === 5;

    if (supportsAvif) return 'avif';
    if (supportsWebP) return 'webp';
    return 'jpg';
  }

  // Monitor Core Web Vitals
  static monitorWebVitals() {
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          console.log('LCP:', lastEntry.renderTime || lastEntry.loadTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // LCP observation not supported
      }

      // First Input Delay
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            console.log('FID:', entry.processingStart - entry.startTime);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // FID observation not supported
      }

      // Cumulative Layout Shift
      try {
        let clsScore = 0;
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsScore += entry.value;
              console.log('CLS:', clsScore);
            }
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // CLS observation not supported
      }
    }
  }
}

// Initialize performance optimizations when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PureOnePerformance();
  });
} else {
  new PureOnePerformance();
}

// Monitor Web Vitals in development
if (process.env.NODE_ENV !== 'production') {
  PureOnePerformance.monitorWebVitals();
}

export default PureOnePerformance;